import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, Tooltip, alpha } from '@mui/material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { useTaskTimerStore } from '../store/taskTimerStore';
import { formatClock } from '../pages/tasks/taskShared';
import { tokens } from '../theme';

/**
 * Running-timer badge in the top bar.
 *
 * Keeps the active measurement visible on every screen and makes it obvious
 * that it survived a page change or a reload — the value is derived from the
 * server-provided start time, not from a browser counter.
 */
export const TaskTimerIndicator: React.FC = () => {
  const navigate = useNavigate();
  const { session, refresh, elapsedSeconds } = useTaskTimerStore();
  const [, tick] = useState(0);

  useEffect(() => {
    refresh();
    // Re-sync with the server every minute in case the session was closed elsewhere.
    const sync = setInterval(refresh, 60_000);
    return () => clearInterval(sync);
  }, [refresh]);

  useEffect(() => {
    if (!session) return undefined;
    const t = setInterval(() => tick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [session]);

  if (!session) return null;

  return (
    <Tooltip title={`Trwa pomiar czasu: ${session.task?.title || 'zadanie'}`}>
      <Chip
        icon={<TimerOutlinedIcon />}
        label={formatClock(elapsedSeconds())}
        onClick={() => navigate(`/tasks/${session.taskId}`)}
        aria-label={`Trwa pomiar czasu zadania ${session.task?.title || ''}: ${formatClock(elapsedSeconds())}. Otwórz zadanie`}
        sx={{
          height: 32,
          fontSize: '0.875rem',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          bgcolor: tokens.successSoft,
          color: tokens.success,
          border: `1px solid ${alpha(tokens.success, 0.2)}`,
          '& .MuiChip-icon': { color: tokens.success, fontSize: 18 },
          '&:hover': { bgcolor: tokens.successSoft, borderColor: alpha(tokens.success, 0.45) },
          display: { xs: 'none', sm: 'inline-flex' },
        }}
      />
    </Tooltip>
  );
};

export default TaskTimerIndicator;
